import React, { useContext, useEffect, useMemo, useState } from "react";
import DocumentMeta from "react-document-meta";
import renderHTML from "react-render-html";
import { Link, useLocation, useParams } from "react-router-dom";
import CommentForm from "../Components/Blog/CommentForm";
import CommentList from "../Components/Blog/CommentList";
import ArticleTools from "../Components/Blog/ArticleTools";
import Loading from "./Loading";
import ContextComponent from "../Context";
import Statics from "../Statics";
import {
  FiArrowRight,
  FiBookOpen,
  FiClock,
  FiUser,
  FiMessageCircle,
  FiChevronDown,
} from "react-icons/fi";
import { isPostDesignPreview, samplePost } from "./Post.preview";
import styles from "./Post.module.css";

const Post = () => {
  const { post_id } = useParams();
  const { search } = useLocation();
  const isPreview = isPostDesignPreview(post_id, search, process.env.NODE_ENV);
  const { ApiDriver } = useContext(ContextComponent);

  const [loadingState, setLoadingState] = useState(Statics.NOT_LOADED);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState(null);
  const [contentsOpen, setContentsOpen] = useState(false);
  useEffect(() => {
    document.body.classList.add("article-page-open");
    return () => document.body.classList.remove("article-page-open");
  }, []);
  const [pageMeta, setPageMeta] = useState({
    language: "fa",
    copyright: "پژوهیار",
    description: "نرم‌افزار استناددهی",
    meta: {
      charset: "utf-8",
      name: {
        keywords: "پژوهیار",
      },
    },
  });

  const postLog = () => {
    ApiDriver.sendBlogLogs("post_visit", "null", post_id, "blog");
  };

  const loadPost = () => {
    setLoadingState(Statics.LOADING);
    ApiDriver.getPost(
      { id: post_id },
      data => {
        setPost(data);
        setLoadingState(Statics.LOADED);

        const metaKeywords = data.tags
          ? data.tags.map(tag => tag.title).join(", ")
          : "";
        setPageMeta({
          title: `${document.title}: ${data.title}`,
          description: data.title,
          meta: {
            charset: "utf-8",
            name: {
              copyright: data.copyright,
              language: data.language,
              keywords: metaKeywords,
            },
          },
        });
      },
      error => {
        console.error(error);
        setLoadingState(Statics.CAUSED_ERROR);
      },
    );
  };

  const loadComments = () => {
    ApiDriver.getComments(
      { post_id },
      data => setComments(data),
      error => console.error(error),
    );
  };

  useEffect(() => {
    if (isPreview) {
      setPost(samplePost);
      setComments([]);
      setPageMeta({
        title: `پژوهیار | پیش‌نمایش مقاله — ${samplePost.title}`,
        description: samplePost.title,
      });
      setLoadingState(Statics.LOADED);
      return;
    }
    loadPost();
    loadComments();
    postLog();
    // eslint-disable-next-line
  }, [post_id, isPreview]);

  const { content, sections } = useMemo(() => {
    const headings = [];
    const textOf = nodes =>
      React.Children.toArray(nodes)
        .map(node =>
          React.isValidElement(node)
            ? textOf(node.props.children)
            : String(node),
        )
        .join("");
    const decorate = nodes =>
      React.Children.map(nodes, node => {
        if (!React.isValidElement(node)) return node;
        const children = decorate(node.props.children);
        if (node.type === "h2" || node.type === "h3") {
          const id = node.props.id || `article-section-${headings.length + 1}`;
          headings.push({
            id,
            title: textOf(children),
            nested: node.type === "h3",
          });
          return React.cloneElement(node, { id }, children);
        }
        const element = React.cloneElement(node, {}, children);
        return node.type === "table" ? (
          <div
            className={styles.tableScroll}
            tabIndex={0}
            role="region"
            aria-label="جدول مقاله">
            {element}
          </div>
        ) : (
          element
        );
      });
    return {
      content: decorate(renderHTML((post && post.body) || "")),
      sections: headings,
    };
  }, [post]);

  const readingMinutes = post
    ? Math.max(
        1,
        Math.ceil(
          (post.body || "")
            .replace(/<[^>]*>/g, " ")
            .trim()
            .split(/\s+/).length / 200,
        ),
      )
    : 1;
  const cover =
    post &&
    post.cover_pic_urls &&
    (post.cover_pic_urls.lg || post.cover_pic_urls.md);
  const published = post && post.published_at;
  const publicationDate =
    published && published.year && published.month && published.day
      ? `${published.year}/${published.month}/${published.day}`
      : null;

  return (
    <DocumentMeta {...pageMeta}>
      <main className={styles.page} dir="rtl">
        <div className={styles.container}>
          {isPreview && (
            <div className={styles.previewNote} role="note">
              پیش‌نمایش طراحی · مقاله‌ی نمونه؛ ارسال نظر غیرفعال است.
            </div>
          )}
          {loadingState === Statics.LOADING && (
            <div className="pajoohyar-loading">
              <Loading />
            </div>
          )}
          {loadingState === Statics.CAUSED_ERROR ? (
            <div className="error-parent">
              <p>ارتباط با سرور با مشکل مواجه شد !</p>
            </div>
          ) : null}

          {loadingState === Statics.LOADED && post && (
            <>
              <nav className={styles.breadcrumb} aria-label="مسیر صفحه">
                <Link to="/blog">
                  <FiArrowRight aria-hidden="true" /> بازگشت به بلاگ
                </Link>
                <span> / </span>
                <span>مقاله</span>
              </nav>
              <header
                className={`${styles.hero} ${
                  !cover ? styles.heroWithoutCover : ""
                }`}>
                <div className={styles.heroContent}>
                  <span className={styles.eyebrow}>
                    <FiBookOpen aria-hidden="true" /> بخوانیم، بهتر پژوهش کنیم
                  </span>
                  <h1>{post.title}</h1>
                  <div className={styles.meta}>
                    {post.author_name && (
                      <span>
                        <FiUser aria-hidden="true" />
                        {post.author_name}
                      </span>
                    )}
                    <span>
                      <FiClock aria-hidden="true" />
                      حدود {readingMinutes.toLocaleString("fa-IR")} دقیقه مطالعه
                    </span>
                    {publicationDate && (
                      <span>
                        انتشار: <bdi>{publicationDate}</bdi>
                      </span>
                    )}
                  </div>
                  {post.tags && post.tags.length > 0 && (
                    <div className={styles.tags} aria-label="موضوع‌های مقاله">
                      {post.tags.map((tag, index) => (
                        <span key={tag.id || index}>{tag.title}</span>
                      ))}
                    </div>
                  )}
                </div>
                {cover && (
                  <div className={styles.cover}>
                    <img src={cover} alt={post.title} />
                  </div>
                )}
              </header>
              <div className={styles.readingLayout}>
                <aside className={styles.sidebar}>
                  {sections.length > 0 && (
                    <nav className={styles.contents} aria-label="فهرست مقاله">
                      <span className={styles.contentsIcon}>
                        <FiBookOpen aria-hidden="true" />
                      </span>
                      <h2 className={styles.contentsHeading}>
                        در این مقاله می‌خوانید
                      </h2>
                      <button
                        type="button"
                        className={styles.contentsToggle}
                        aria-expanded={contentsOpen}
                        aria-controls="article-contents"
                        onClick={() => setContentsOpen(!contentsOpen)}>
                        <span>
                          <span className={styles.contentsIcon}>
                            <FiBookOpen aria-hidden="true" />
                          </span>
                          در این مقاله می‌خوانید
                        </span>
                        <FiChevronDown
                          aria-hidden="true"
                          className={contentsOpen ? styles.rotated : ""}
                        />
                      </button>
                      <ol
                        id="article-contents"
                        className={`${styles.contentsList} ${
                          contentsOpen ? styles.contentsExpanded : ""
                        }`}>
                        {sections.map(section => (
                          <li
                            key={section.id}
                            className={section.nested ? styles.nested : ""}>
                            <a href={`#${section.id}`}>{section.title}</a>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  )}
                  <ArticleTools
                    url={`${
                      window.location.origin
                    }/blog/post/${encodeURIComponent(post.id)}${
                      isPreview ? "?preview=1" : ""
                    }`}
                  />
                </aside>
                <div className={styles.mainColumn}>
                  <article className={styles.article} aria-label={post.title}>
                    <div className={styles.body}>{content}</div>
                    {post.author_name && (
                      <footer className={styles.author}>
                        <span className={styles.authorIcon}>
                          <FiUser aria-hidden="true" />
                        </span>
                        <div>
                          <span>نویسنده‌ی این مقاله</span>
                          <strong>{post.author_name}</strong>
                        </div>
                        <Link to="/blog">
                          مطالب بیشتر <FiArrowRight aria-hidden="true" />
                        </Link>
                      </footer>
                    )}
                  </article>
                  <section
                    className={styles.comments}
                    aria-label="دیدگاه‌های مقاله">
                    <div className={styles.commentIntro}>
                      <span>
                        <FiMessageCircle aria-hidden="true" />
                      </span>
                      <div>
                        <h2>گفت‌وگو ادامه دارد</h2>
                        <p>
                          تجربه یا پرسش خود را درباره‌ی این مقاله با ما در میان
                          بگذارید.
                        </p>
                      </div>
                    </div>
                    <CommentForm
                      form_title="ارسال دیدگاه"
                      post_id={post.id}
                      disabled={isPreview}
                    />
                    {comments && comments.length > 0 && (
                      <div className={styles.commentList}>
                        <h2>دیدگاه‌های خوانندگان</h2>
                        <CommentList comments={comments} post_id={post.id} />
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </DocumentMeta>
  );
};

export default Post;
